(() => {
  const TRANSITION_DELAY = 560;

  const prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;

  const isThemeEditor = Boolean(
    window.Shopify && window.Shopify.designMode
  );

  const isMobileNavigation = window.matchMedia(
    '(max-width: 749px), (pointer: coarse)'
  ).matches;

  /*
    IMPORTANT:
    On phones, do not register any click, pointer, submit, ripple, or
    page-transition listeners. Shopify must control its mobile drawer
    completely on its own.
  */
  if (isMobileNavigation) {
    const cleanMobileState = () => {
      document.body?.classList.remove(
        'astro-page-leaving',
        'astro-page-arriving',
        'astro-page-arrived'
      );

      document
        .querySelectorAll('.astro-page-transition, .astro-click-ripple')
        .forEach((element) => element.remove());

      document
        .querySelectorAll(
          '.astro-button-launching, .astro-button-adding'
        )
        .forEach((element) => {
          element.classList.remove(
            'astro-button-launching',
            'astro-button-adding'
          );
        });
    };

    if (document.readyState === 'loading') {
      document.addEventListener(
        'DOMContentLoaded',
        cleanMobileState,
        { once: true }
      );
    } else {
      cleanMobileState();
    }

    window.addEventListener('pageshow', cleanMobileState);
    return;
  }

  function findLogoSource() {
    const selectors = [
      '#header-component .header-logo__image',
      '#header-component img',
      '.header__heading-logo img',
      '.header-logo img',
      'header img[alt*="Astro"]',
      'header img'
    ];

    for (const selector of selectors) {
      const image = document.querySelector(selector);

      if (image?.currentSrc || image?.src) {
        return image.currentSrc || image.src;
      }
    }

    return '';
  }

  function createTransitionLayer() {
    if (document.querySelector('.astro-page-transition')) return;

    const layer = document.createElement('div');
    const logoSource = findLogoSource();

    layer.className = 'astro-page-transition';
    layer.setAttribute('aria-hidden', 'true');

    layer.innerHTML = `
      <div class="astro-page-transition__beam"></div>
      <div class="astro-page-transition__center">
        <div class="astro-page-transition__orbit"></div>
        <div class="astro-page-transition__logo-wrap">
          ${
            logoSource
              ? `<img class="astro-page-transition__logo" src="${logoSource}" alt="">`
              : `<span class="astro-page-transition__fallback">Astro Deck</span>`
          }
        </div>
      </div>
    `;

    document.body.appendChild(layer);
  }

  function isModifiedClick(event) {
    return Boolean(
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      event.button !== 0
    );
  }

  function getInternalDestination(link) {
    const href = link.getAttribute('href');

    if (
      !href ||
      href.startsWith('#') ||
      href.startsWith('mailto:') ||
      href.startsWith('tel:') ||
      href.startsWith('javascript:')
    ) {
      return null;
    }

    if (
      link.hasAttribute('download') ||
      link.target === '_blank' ||
      link.dataset.noTransition !== undefined
    ) {
      return null;
    }

    let destination;

    try {
      destination = new URL(link.href, window.location.href);
    } catch (error) {
      return null;
    }

    if (
      destination.origin !== window.location.origin ||
      destination.href === window.location.href
    ) {
      return null;
    }

    if (
      destination.pathname === window.location.pathname &&
      destination.search === window.location.search &&
      destination.hash
    ) {
      return null;
    }

    return destination.href;
  }

  /*
    Animate only real storefront CTA controls.
    Never include the generic "button" selector, because Shopify's menu,
    search, account, cart, quantity, and drawer controls are buttons too.
  */
  function findAnimatedControl(target) {
    return target.closest(`
      .button,
      .shopify-payment-button__button,
      .astro-product__add,
      .astro-product__add-button,
      .adhero__button,
      [data-add-to-cart]
    `);
  }

  function addRipple(element, event) {
    if (
      prefersReducedMotion ||
      !element ||
      (element.tagName === 'A' &&
        !element.classList.contains('button') &&
        !element.classList.contains('adhero__button'))
    ) {
      return;
    }

    const bounds = element.getBoundingClientRect();
    const ripple = document.createElement('span');

    ripple.className = 'astro-click-ripple';

    const x = event.clientX
      ? event.clientX - bounds.left
      : bounds.width / 2;

    const y = event.clientY
      ? event.clientY - bounds.top
      : bounds.height / 2;

    ripple.style.left = `${x}px`;
    ripple.style.top = `${y}px`;

    element.appendChild(ripple);
    window.setTimeout(() => ripple.remove(), 680);
  }

  function beginPageTransition(link, destination) {
    if (
      prefersReducedMotion ||
      isThemeEditor ||
      document.body.classList.contains('astro-page-leaving')
    ) {
      window.location.href = destination;
      return;
    }

    link.classList.add('astro-button-launching');
    document.body.classList.add('astro-page-leaving');

    window.setTimeout(() => {
      window.location.href = destination;
    }, TRANSITION_DELAY);
  }

  document.addEventListener('pointerdown', (event) => {
    const control = findAnimatedControl(event.target);
    if (!control) return;

    addRipple(control, event);
  });

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || isModifiedClick(event)) return;

    const link = event.target.closest('a[href]');
    if (!link) return;

    const destination = getInternalDestination(link);
    if (!destination) return;

    event.preventDefault();
    beginPageTransition(link, destination);
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;

    if (!(form instanceof HTMLFormElement)) return;

    const action = form.getAttribute('action') || '';
    if (!action.includes('/cart/add')) return;

    const submitter =
      event.submitter ||
      form.querySelector('[type="submit"], [data-add-to-cart]');

    if (!submitter) return;

    submitter.classList.add('astro-button-adding');

    window.setTimeout(() => {
      submitter.classList.remove('astro-button-adding');
    }, 650);
  });

  function clearDesktopTransitionState() {
    document.body.classList.remove(
      'astro-page-leaving',
      'astro-page-arriving',
      'astro-page-arrived'
    );

    document
      .querySelectorAll('.astro-button-launching')
      .forEach((element) => {
        element.classList.remove('astro-button-launching');
      });
  }

  window.addEventListener('pageshow', (event) => {
    clearDesktopTransitionState();

    if (event.persisted) {
      document
        .querySelectorAll('.astro-page-transition')
        .forEach((layer) => {
          layer.style.opacity = '0';
          layer.style.visibility = 'hidden';
        });
    }
  });

  function initialize() {
    createTransitionLayer();

    /*
      Play the logo only while leaving the current page.
      Do not replay it after the destination page has appeared.
    */
    document.body.classList.remove(
      'astro-page-arriving',
      'astro-page-arrived'
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialize,
      { once: true }
    );
  } else {
    initialize();
  }
})();